/**
 * `@npc/platform` —— Web 与 macOS 之间**唯一**的本机能力接缝。
 *
 * 用法只有一句：
 *
 * ```ts
 * import { copyText } from '@npc/platform'
 * const r = await copyText(token)
 * if (!r.ok) { ... }   // r.reason: 'unsupported' | 'denied' | 'failed'
 * ```
 *
 * 它替代的是 `navigator.clipboard.writeText(...)` 那一行。守着这件事的不是
 * 这段注释，是 `apps/console/src/app/platform-boundary.test.ts`——
 * 谁在控制台生产代码里再写一次那一行，它当场红。
 *
 * 包的边界、为什么只有剪贴板、以及数过之后**没有**放进来的那几个能力，
 * 见 `types.ts` 里 `PlatformCapabilities` 上面那张表。
 */
import { detectSurface } from './surface'
import { macosCapabilities } from './tauri'
import { webCapabilities } from './web'
import type { HostLike, Outcome, PlatformCapabilities, Surface } from './types'

export { detectSurface } from './surface'
export type {
  ClipboardCapability,
  HostLike,
  Outcome,
  OutcomeFailure,
  PlatformCapabilities,
  Surface,
} from './types'

/**
 * 每一档壳一个工厂。
 *
 * 类型写成 `{ readonly [S in Surface]: ... }` 而不是 `Record<string, ...>`，
 * 是**故意的**：`Surface` 加一档（Capacitor 那份将来是 `'ios'` / `'android'`）
 * 而这里没跟着加，这一行当场编不过。
 *
 * 这就是 CLAUDE.md §8 说的那件事——「两边必须一起改」的约定要交给编译器，
 * 不要交给注释。注释会忘，编译器不会。
 */
const FACTORIES: { readonly [S in Surface]: (host: HostLike) => PlatformCapabilities } = {
  web: webCapabilities,
  macos: macosCapabilities,
}

/**
 * 上面那张表的两个方向，各写成一句编译期断言。
 *
 * 映射类型本身已经保证了「每一档都有工厂」；这两行守的是**下一个人**：
 * 有人把 `FACTORIES` 的类型放宽成 `Record<string, …>` 的那一刻，
 * 第一行就编不过，而不是等到某一档在运行时取不到工厂。
 *
 * 导出是为了不被 `noUnusedLocals` 判成没用的声明——
 * 与 `packages/copy/src/compile-guards.test.ts` 里 `neverCalled` 同一个理由。
 */
type _EverySurfaceHasAFactory = Exclude<Surface, keyof typeof FACTORIES> extends never ? true : never
type _NoFactoryWithoutASurface = Exclude<keyof typeof FACTORIES, Surface> extends never ? true : never
export const _factoryCoverage: readonly [_EverySurfaceHasAFactory, _NoFactoryWithoutASurface] = [
  true,
  true,
]

function hostOf(host: unknown): HostLike {
  return typeof host === 'object' && host !== null ? (host as HostLike) : {}
}

/**
 * 这个壳能做的事。
 *
 * `host` 只在用例里显式传（要一个假的 `navigator`、或者一个带
 * `__TAURI_INTERNALS__` 的假宿主时）。生产代码不传。
 */
export function platform(host: unknown = globalThis): PlatformCapabilities {
  return FACTORIES[detectSurface(host)](hostOf(host))
}

/**
 * 把一段文本放进系统剪贴板。**不抛。**
 *
 * 这是控制台那八处唯一该调的东西。
 */
export function copyText(text: string, host: unknown = globalThis): Promise<Outcome> {
  return platform(host).clipboard.copyText(text)
}
